import { FormEvent, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { AdminFrame } from './AdminFrame';
import { AppError, errorText, NATIONAL_LEVELS, ops, PaperworkStatus, RoleSpec, rpc, SchoolType } from './api';
import { CopyButton, LevelPicker, TIER_B_AVAILABLE, TierPicker } from './bits';

type Created = { id: string; name: string; code: string; type: SchoolType };
type Credential = { name: string; roles: string; username: string; password: string };
type PersonOutcome = { name: string; ok: boolean; error?: AppError };

const STEPS = ['type', 'details', 'paperwork', 'people', 'review'] as const;
type Step = (typeof STEPS)[number];

// A1 — Create school, as a short wizard on one page: type → details →
// paperwork → first people → review. Creating it also enters support mode
// for the new school (logged) to set up the first people: test accounts in
// a Sandbox school (passwords shown once), queued invites in a Customer
// school (sent once an email provider exists).
export function AdminAddSchool() {
  const { t } = useI18n();
  return (
    <AdminFrame section="schools" title={t['overview.addSchool']}>
      {({ refreshSupport }) => <Wizard refreshSupport={refreshSupport} />}
    </AdminFrame>
  );
}

function Wizard({ refreshSupport }: { refreshSupport: () => Promise<void> }) {
  const { t, locale } = useI18n();
  const announce = useAnnounce();
  const ids = useId();
  const stepHeading = useRef<HTMLHeadingElement>(null);

  const [step, setStep] = useState<Step>('type');
  const [type, setType] = useState<SchoolType>('sandbox');
  const [name, setName] = useState('');
  const [municipality, setMunicipality] = useState('');
  const [municipalities, setMunicipalities] = useState<{ code: string; name: string }[]>([]);
  const [officialNumber, setOfficialNumber] = useState('');
  const [stages, setStages] = useState<string[]>(['vl7', 'vl8', 'vl9']);
  const [language, setLanguage] = useState<'fi' | 'sv' | 'en'>('fi');
  // Tier A by default while Tier B isn't offered (0014).
  const [tier, setTier] = useState(TIER_B_AVAILABLE ? '' : 'tier_a');
  const [pw, setPw] = useState<PaperworkStatus>('not_started');
  const [pwDate, setPwDate] = useState('');
  const [pwSignatory, setPwSignatory] = useState('');
  const [headName, setHeadName] = useState('');
  const [headEmail, setHeadEmail] = useState('');
  const [samePerson, setSamePerson] = useState(true);
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [stepError, setStepError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<Created | null>(null);
  const [creds, setCreds] = useState<Credential[]>([]);
  const [outcomes, setOutcomes] = useState<PersonOutcome[]>([]);

  useEffect(() => {
    rpc<{ code: string; name: string }[]>('platform_municipalities').then((r) => r.data && setMunicipalities(r.data));
  }, []);

  // Move focus to the new step's heading when the step changes (not on the
  // first load, where the page heading already has it).
  const firstStep = useRef(true);
  useEffect(() => {
    if (firstStep.current) {
      firstStep.current = false;
      return;
    }
    stepHeading.current?.focus();
  }, [step]);

  const idx = STEPS.indexOf(step);
  const sandbox = type === 'sandbox';
  const today = new Date().toISOString().slice(0, 10);
  const emailOk = (e: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e.trim());

  function validate(s: Step): string | null {
    if (s === 'details') {
      if (!name.trim()) return t['err.name_required'];
      if (!municipality) return t['err.unknown_municipality'];
      if (stages.length === 0) return t['pa.wizStagesNeeded'];
      if (!sandbox && !tier) return t['err.tier_required'];
    }
    if (s === 'paperwork' && pw === 'signed' && (!pwDate || !pwSignatory.trim())) return t['err.paperwork_signed_needs'];
    if (s === 'people') {
      if (!headName.trim()) return t['pa.wizHeadNeeded'];
      if (!sandbox && !emailOk(headEmail)) return t['err.bad_email'];
      if (!samePerson && !adminName.trim()) return t['pa.wizAdminNeeded'];
      if (!samePerson && !sandbox && !emailOk(adminEmail)) return t['err.bad_email'];
    }
    return null;
  }

  function next(e: FormEvent) {
    e.preventDefault();
    const err = validate(step);
    setStepError(err);
    if (err) return;
    if (step === 'review') return create();
    setStep(STEPS[idx + 1]);
  }

  function back() {
    setStepError(null);
    setStep(STEPS[idx - 1]);
  }

  function rolesFor(head: boolean, admin: boolean): RoleSpec[] {
    const r: RoleSpec[] = [];
    if (head) r.push({ role: 'head_of_school', is_primary: true });
    if (admin) r.push({ role: 'school_admin' });
    return r;
  }

  async function create() {
    setBusy(true);
    setStepError(null);
    const res = await rpc<Created & { slug: string }>('platform_create_school', {
      p_name: name.trim(),
      p_school_type: type,
      p_municipality: municipality,
      p_stages: stages,
      p_default_language: language,
      p_official_number: officialNumber.trim() || null,
      p_tier: sandbox ? null : tier,
      p_paperwork_status: pw,
      p_paperwork_signed_on: pw === 'signed' ? pwDate : null,
      p_paperwork_signatory: pw === 'signed' ? pwSignatory.trim() : null,
    });
    if (res.error) {
      setBusy(false);
      setStepError(errorText(t, res.error));
      return;
    }
    const school = res.data;
    const sup = await rpc('platform_enter_support', {
      p_school_id: school.id,
      p_reason: sandbox ? 'test_accounts' : 'other',
      p_note: sandbox ? null : 'Onboarding: first people (A1)',
    });
    const people: { name: string; email: string; roles: RoleSpec[] }[] = samePerson
      ? [{ name: headName.trim(), email: headEmail.trim(), roles: rolesFor(true, true) }]
      : [
          { name: headName.trim(), email: headEmail.trim(), roles: rolesFor(true, false) },
          { name: adminName.trim(), email: adminEmail.trim(), roles: rolesFor(false, true) },
        ];
    const got: Credential[] = [];
    const out: PersonOutcome[] = [];
    for (const p of people) {
      if (sup.error) {
        out.push({ name: p.name, ok: false, error: sup.error });
        continue;
      }
      if (sandbox) {
        const r = await ops<{ username: string; password: string; pending_roles?: string[] }>('create_test_account', {
          school_id: school.id,
          display_name: p.name,
          roles: p.roles,
          expires_days: 90,
        });
        if (r.error) out.push({ name: p.name, ok: false, error: r.error });
        else {
          out.push({ name: p.name, ok: true });
          got.push({
            name: p.name,
            roles: p.roles
              .map((x) => {
                const label = x.role === 'head_of_school' ? t['pa.rolePrimaryHead'] : t['role.schoolAdmin'];
                // A widening role added after the primary head waits for their approval (0011).
                return r.data.pending_roles?.includes(x.role) ? t['pa.rolePending'](label) : label;
              })
              .join(', '),
            username: r.data.username,
            password: r.data.password,
          });
        }
      } else {
        const r = await rpc('platform_queue_invite', {
          p_school_id: school.id,
          p_email: p.email,
          p_display_name: p.name,
          p_roles: p.roles,
          p_language: language,
        });
        out.push({ name: p.name, ok: !r.error, error: r.error ?? undefined });
      }
    }
    setCreds(got);
    setOutcomes(out);
    setCreated({ id: school.id, name: school.name, code: school.code, type });
    setBusy(false);
    announce(t['admin.schoolAdded'](school.name));
    await refreshSupport();
  }

  function reset() {
    setCreated(null);
    setCreds([]);
    setOutcomes([]);
    setStep('type');
    setName('');
    setOfficialNumber('');
    setHeadName('');
    setHeadEmail('');
    setAdminName('');
    setAdminEmail('');
    setPw('not_started');
    setPwDate('');
    setPwSignatory('');
  }

  const crumbs = (
    <p className="crumbs">
      <Link to="/admin">{t['admin.navOverview']}</Link> › <Link to="/admin/schools">{t['admin.navSchools']}</Link> ›{' '}
      <span aria-current="page">{t['overview.addSchool']}</span>
    </p>
  );

  if (created) {
    const failed = outcomes.filter((o) => !o.ok);
    return (
      <>
        {crumbs}
        <section className="panel p5 form-page" aria-labelledby="done-title">
          <h2 className="panel-title" id="done-title" ref={stepHeading} tabIndex={-1}>
            {t['admin.schoolAdded'](created.name)}
          </h2>
          <p>
            {t['addSchool.codeIs']} <span className="code-chip">{created.code}</span>{' '}
            <CopyButton text={created.code} label={t['pa.copyCode']} />
          </p>
          <p className="form-hint">{t['pa.codeHint']}</p>
          {creds.length > 0 && (
            <div className="reveal" role="region" aria-label={t['pa.credsTitle']}>
              <p>
                <strong>{t['pa.credsTitle']}</strong>
              </p>
              <p className="form-hint">{t['pa.credsOnce']}</p>
              <ul className="creds">
                {creds.map((c) => (
                  <li key={c.username}>
                    <p>
                      <strong>{c.name}</strong> · {c.roles}
                    </p>
                    <p>
                      {t['pa.username']}: <code>{c.username}</code> <CopyButton text={c.username} label={t['pa.copyUsername']} />
                    </p>
                    <p>
                      {t['pa.password']}: <code className="secret">{c.password}</code>{' '}
                      <CopyButton text={c.password} label={t['pa.copyPassword']} />
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!sandbox && outcomes.some((o) => o.ok) && <p className="notice">{t['pa.invitesQueuedNote']}</p>}
          {failed.length > 0 && (
            <div className="error-box" role="alert">
              <p>{t['pa.peopleFailed']}</p>
              <ul>
                {failed.map((f) => (
                  <li key={f.name}>
                    {f.name}: {errorText(t, f.error)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="form-actions">
            <Link className="btn btn-primary" to={`/admin/schools/${created.id}`}>
              {t['pa.openSchool']}
            </Link>
            <button type="button" className="btn btn-ghost" onClick={reset}>
              {t['addSchool.another']}
            </button>
            <Link className="btn btn-ghost" to="/admin/schools">
              {t['addSchool.backToList']}
            </Link>
          </div>
        </section>
      </>
    );
  }

  const stepTitle: Record<Step, string> = {
    type: t['pa.wizType'],
    details: t['pa.wizDetails'],
    paperwork: t['pa.wizPaperwork'],
    people: t['pa.wizPeople'],
    review: t['pa.wizReview'],
  };
  const muniName = municipalities.find((m) => m.code === municipality)?.name ?? municipality;

  return (
    <>
      {crumbs}
      <section className="panel p5 form-page" aria-labelledby={`${ids}-step`}>
        <ol className="stepper" aria-label={t['pa.wizSteps']}>
          {STEPS.map((s, i) => (
            <li key={s} className={i === idx ? 'on' : i < idx ? 'done' : ''} aria-current={i === idx ? 'step' : undefined}>
              <span className="stepper-n" aria-hidden="true">
                {i < idx ? '✓' : i + 1}
              </span>
              <span>{stepTitle[s]}</span>
              {i < idx && <span className="visually-hidden"> ({t['pa.done']})</span>}
            </li>
          ))}
        </ol>
        <h2 className="panel-title" id={`${ids}-step`} ref={stepHeading} tabIndex={-1}>
          {t['pa.wizStepOf'](idx + 1, STEPS.length, stepTitle[step])}
        </h2>

        <form className="form" onSubmit={next} noValidate>
          {step === 'type' && (
            <fieldset className="choice-list">
              <legend className="field-label">{t['pa.schoolType']}</legend>
              {(['sandbox', 'customer'] as const).map((v) => (
                <label key={v} className={`choice${type === v ? ' on' : ''}`}>
                  <input type="radio" name="type" value={v} checked={type === v} onChange={() => setType(v)} />
                  <span>
                    <strong>{v === 'sandbox' ? t['school.sandbox'] : t['school.customer']}</strong>
                    <span className="choice-desc">{v === 'sandbox' ? t['pa.sandboxDesc'] : t['pa.customerDesc']}</span>
                  </span>
                </label>
              ))}
              <p className="form-hint">{t['pa.typeHint']}</p>
            </fieldset>
          )}

          {step === 'details' && (
            <>
              <div className="field-group">
                <label className="field-label" htmlFor={`${ids}-name`}>
                  {t['admin.schoolName']}
                </label>
                <input id={`${ids}-name`} className="field" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" />
              </div>
              <div className="field-group">
                <label className="field-label" htmlFor={`${ids}-muni`}>
                  {t['pa.municipality']}
                </label>
                <span className="select-wrap">
                  <select id={`${ids}-muni`} className="select" value={municipality} onChange={(e) => setMunicipality(e.target.value)} required aria-describedby={`${ids}-muni-hint`}>
                    <option value="">{t['pa.chooseMunicipality']}</option>
                    {municipalities.map((m) => (
                      <option key={m.code} value={m.code}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </span>
                <p className="form-hint" id={`${ids}-muni-hint`}>
                  {t['pa.codeFromMunicipality']}
                </p>
              </div>
              <div className="field-group">
                <label className="field-label" htmlFor={`${ids}-num`}>
                  {t['pa.officialNumber']}
                </label>
                <input id={`${ids}-num`} className="field" inputMode="numeric" value={officialNumber} onChange={(e) => setOfficialNumber(e.target.value)} aria-describedby={`${ids}-num-hint`} />
                <p className="form-hint" id={`${ids}-num-hint`}>
                  {t['pa.optional']}
                </p>
              </div>
              <LevelPicker value={stages} onChange={setStages} legend={t['pa.stages']} hint={t['pa.stagesHint']} />
              <fieldset className="inline-radios">
                <legend className="field-label">{t['pa.defaultLanguage']}</legend>
                {(['fi', 'sv', 'en'] as const).map((l) => (
                  <label key={l}>
                    <input type="radio" name="lang" checked={language === l} onChange={() => setLanguage(l)} /> {t[`pa.lang_${l}`]}
                  </label>
                ))}
              </fieldset>
              {!sandbox && <TierPicker value={tier} onChange={setTier} />}
            </>
          )}

          {step === 'paperwork' && (
            <>
              <p className="form-hint">{sandbox ? t['pa.paperworkSandboxHint'] : t['pa.paperworkCustomerHint']}</p>
              <fieldset className="inline-radios">
                <legend className="field-label">{sandbox ? t['pa.agreement'] : t['pa.dpa']}</legend>
                {(['not_started', 'sent', 'signed'] as const).map((v) => (
                  <label key={v}>
                    <input type="radio" name="pw" checked={pw === v} onChange={() => setPw(v)} />{' '}
                    {v === 'signed' ? t['pa.pwSigned'] : v === 'sent' ? t['pa.pwSent'] : t['pa.pwNotStarted']}
                  </label>
                ))}
              </fieldset>
              {pw === 'signed' && (
                <>
                  <div className="field-group">
                    <label className="field-label" htmlFor={`${ids}-pwd`}>
                      {t['pa.signedOn']}
                    </label>
                    <input id={`${ids}-pwd`} className="field" type="date" max={today} value={pwDate} onChange={(e) => setPwDate(e.target.value)} />
                  </div>
                  <div className="field-group">
                    <label className="field-label" htmlFor={`${ids}-pws`}>
                      {t['pa.signatory']}
                    </label>
                    <input id={`${ids}-pws`} className="field" value={pwSignatory} onChange={(e) => setPwSignatory(e.target.value)} autoComplete="off" />
                  </div>
                </>
              )}
            </>
          )}

          {step === 'people' && (
            <>
              <p className="notice">{sandbox ? t['pa.peopleSandboxNote'] : t['pa.peopleCustomerNote']}</p>
              <div className="field-group">
                <label className="field-label" htmlFor={`${ids}-hn`}>
                  {sandbox ? t['pa.headNameTest'] : t['pa.headName']}
                </label>
                <input id={`${ids}-hn`} className="field" value={headName} onChange={(e) => setHeadName(e.target.value)} autoComplete="off" placeholder={sandbox ? t['pa.fictionalExample'] : undefined} />
              </div>
              {!sandbox && (
                <div className="field-group">
                  <label className="field-label" htmlFor={`${ids}-he`}>
                    {t['pa.workEmail']}
                  </label>
                  <input id={`${ids}-he`} className="field" type="email" value={headEmail} onChange={(e) => setHeadEmail(e.target.value)} autoComplete="off" />
                </div>
              )}
              <label className="check">
                <input type="checkbox" checked={samePerson} onChange={(e) => setSamePerson(e.target.checked)} /> {t['pa.sameAdmin']}
              </label>
              {!samePerson && (
                <>
                  <div className="field-group">
                    <label className="field-label" htmlFor={`${ids}-an`}>
                      {sandbox ? t['pa.adminNameTest'] : t['pa.adminName']}
                    </label>
                    <input id={`${ids}-an`} className="field" value={adminName} onChange={(e) => setAdminName(e.target.value)} autoComplete="off" />
                  </div>
                  {!sandbox && (
                    <div className="field-group">
                      <label className="field-label" htmlFor={`${ids}-ae`}>
                        {t['pa.workEmail']}
                      </label>
                      <input id={`${ids}-ae`} className="field" type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} autoComplete="off" />
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {step === 'review' && (
            <dl className="kv">
              <div>
                <dt>{t['pa.schoolType']}</dt>
                <dd>{sandbox ? t['school.sandbox'] : t['school.customer']}</dd>
              </div>
              <div>
                <dt>{t['admin.schoolName']}</dt>
                <dd>{name}</dd>
              </div>
              <div>
                <dt>{t['pa.municipality']}</dt>
                <dd>{muniName}</dd>
              </div>
              <div>
                <dt>{t['pa.stages']}</dt>
                <dd>{NATIONAL_LEVELS.filter((l) => stages.includes(l)).map((l) => t[`lvl.${l}`]).join(', ')}</dd>
              </div>
              <div>
                <dt>{t['pa.defaultLanguage']}</dt>
                <dd>{t[`pa.lang_${language}`]}</dd>
              </div>
              {!sandbox && (
                <div>
                  <dt>{t['pa.tier']}</dt>
                  <dd>{tier ? t[`pa.${tier as 'tier_a' | 'tier_b'}Short`] : '–'}</dd>
                </div>
              )}
              <div>
                <dt>{sandbox ? t['pa.agreement'] : t['pa.dpa']}</dt>
                <dd>
                  {pw === 'signed'
                    ? t['pa.signedBy'](pwSignatory, new Date(pwDate).toLocaleDateString(locale))
                    : pw === 'sent'
                      ? t['pa.pwSent']
                      : t['pa.pwNotStarted']}
                </dd>
              </div>
              <div>
                <dt>{t['pa.firstPeople']}</dt>
                <dd>
                  {samePerson
                    ? t['pa.reviewSame'](headName)
                    : t['pa.reviewTwo'](headName, adminName)}
                </dd>
              </div>
              <div>
                <dt>{t['pa.afterCreate']}</dt>
                <dd>{sandbox ? t['pa.afterCreateSandbox'] : t['pa.afterCreateCustomer']}</dd>
              </div>
            </dl>
          )}

          {stepError && (
            <p className="error-text" role="alert">
              {stepError}
            </p>
          )}
          <div className="form-actions">
            {idx > 0 && (
              <button type="button" className="btn btn-ghost" onClick={back} disabled={busy}>
                {t['pa.back']}
              </button>
            )}
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? t['admin.adding'] : step === 'review' ? t['pa.createSchool'] : t['pa.next']}
            </button>
            <Link className="btn btn-ghost" to="/admin/schools">
              {t['form.cancel']}
            </Link>
          </div>
        </form>
      </section>
    </>
  );
}
